import { useCallback, useState, useLayoutEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@apollo/react-hooks';

import { useSortByObjectField, CheckboxField } from '../../../../../common';
import { SelectablePlaylistButton } from '../../../shared';
import MusicChannel from './musicChannel';
import { UPDATE_MUSIC_CHANNEL_GROUP_VISIBILITY } from '../api';
import { useUpdateChannelGroupCacheAfterUpdate } from '../cache';

const Container = styled.div`
  display: grid;
  ${({ collapsed }) => collapsed && 'opacity: 0.7;'}
`;

const StyledSelectablePlaylistButton = styled(SelectablePlaylistButton)`
  padding: 0.85rem 0.5rem 0.85rem 1.5rem;

  &:after {
    width: 100%;
  }
`;

const Channels = ({ channel, musicProfile, channels, openedSubCollapsible, setOpenedSubCollapsible }) => {
  const sortedChannels = useSortByObjectField(channels, 'name');

  const firstRender = useRef(true);

  const [isSelected, setIsSelected] = useState(channel?.properties?.visible ?? false);
  const areChildrenActive = useMemo(
    () => channels.some((musicChannel) => musicChannel.properties.visible === true),
    [channels]
  );
  const areAllChildrenActive = useMemo(
    () => channels.every((musicChannel) => musicChannel.properties.visible === true),
    [channels]
  );

  const handleUpdateChannelGroupCacheAfterUpdate = useUpdateChannelGroupCacheAfterUpdate({
    musicProfileId: musicProfile.tunifyMusicProfileUserId,
    musicChannelGroupId: channel.id,
    shouldUpdateChildren: false,
  });

  const handleUpdateChannelGroupWithChildrenCacheAfterUpdate = useUpdateChannelGroupCacheAfterUpdate({
    musicProfileId: musicProfile.tunifyMusicProfileUserId,
    musicChannelGroupId: channel.id,
    shouldUpdateChildren: true,
  });

  const [updateMusicChannelGroupVisibility] = useMutation(UPDATE_MUSIC_CHANNEL_GROUP_VISIBILITY, {
    onCompleted: (data) => {
      setIsSelected(data?.updateMusicChannelGroupVisibility?.visible);
    },
  });

  useLayoutEffect(() => {
    if (firstRender.current) {
      return;
    }
    // Update the parent checkbox depending on all the children checkbox statuses
    setIsSelected(areChildrenActive);
    updateMusicChannelGroupVisibility({
      update: handleUpdateChannelGroupCacheAfterUpdate,
      variables: {
        musicProfileId: musicProfile.tunifyMusicProfileUserId,
        musicChannelGroupId: channel.id,
        visible: areChildrenActive,
      },
    });
  }, [
    areChildrenActive,
    channel.id,
    handleUpdateChannelGroupCacheAfterUpdate,
    musicProfile.tunifyMusicProfileUserId,
    updateMusicChannelGroupVisibility,
  ]);

  const collapsed = openedSubCollapsible !== channel.id;

  const handleOpenCollapsible = useCallback(() => {
    if (collapsed) {
      setOpenedSubCollapsible(channel.id);
    } else {
      setOpenedSubCollapsible('');
    }
  }, [setOpenedSubCollapsible, collapsed, channel]);

  const handleClickCheckbox = useCallback((event) => event.stopPropagation(), []);

  const handleChangeCheckbox = useCallback(() => {
    updateMusicChannelGroupVisibility({
      update: handleUpdateChannelGroupWithChildrenCacheAfterUpdate,
      variables: {
        musicProfileId: musicProfile.tunifyMusicProfileUserId,
        musicChannelGroupId: channel.id,
        visible: !isSelected,
        childrenIds: sortedChannels.map((ch) => ch.id),
      },
    });
    setIsSelected(!isSelected);
  }, [
    updateMusicChannelGroupVisibility,
    musicProfile.tunifyMusicProfileUserId,
    channel.id,
    isSelected,
    sortedChannels,
    handleUpdateChannelGroupWithChildrenCacheAfterUpdate,
  ]);

  return (
    <Container>
      <StyledSelectablePlaylistButton onClick={handleOpenCollapsible} type="button">
        <p>{channel.name}</p>
        <CheckboxField
          onChange={handleChangeCheckbox}
          onClick={handleClickCheckbox}
          checked={isSelected || areAllChildrenActive}
        />
      </StyledSelectablePlaylistButton>
      <AnimatePresence>
        {!collapsed ? (
          <motion.div
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            initial={{ height: 0 }}
            transition={{ ease: 'easeInOut', duration: 0.3 }}
          >
            {sortedChannels?.length > 0
              ? sortedChannels.map((musicChannel) => (
                  <MusicChannel
                    key={musicChannel.id}
                    channel={channel}
                    groupCheckbox={isSelected}
                    musicChannel={musicChannel}
                  />
                ))
              : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Container>
  );
};

export default Channels;
